import { Box, Button, Flex, Input, Field, Text, Link, Image, chakra } from '@chakra-ui/react'
import { PasswordInput } from '@/components/ui/password-input'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { useFormik } from 'formik'
import { LuLogIn } from 'react-icons/lu'
import { useNavigate } from 'react-router-dom'
import { loginApi, resendSignupEmailApi } from '@/api/auth.api'
import Logo from '../../assets/logo.png'
import { SigninSchema, INITIAL_SIGNIN_VALUES, type SigninData } from '@/common/auth.common'
import { normalizeRole } from '@/utils/normalizeRole'
import { ROLES } from '@/constants/roles.constants'
import { UNION_MANAGER_ROUTES } from '@/constants/unionRoutes.constants'
import { useUserStore } from '@/stores/user.store'
import type { UserRole } from '@/common/user.common'
import { useState } from 'react'

export default function LoginPage() {
  const navigate = useNavigate()
  const setUser = useUserStore(s => s.setUser)
  // const [showMobileNotSupported, setShowMobileNotSupported] = useState(false)
  const [authErrorCode, setAuthErrorCode] = useState<string | undefined>(undefined)
  const [resendingVerification, setResendingVerification] = useState(false)

  function redirectByRole(role: UserRole): string {
    if (role === ROLES.ADMIN) return '/bo/dashboard'
    if (role === ROLES.MANAGER) return UNION_MANAGER_ROUTES.orders
    if (role === ROLES.USER) return '/app/user/dashboard'
    return '/app/dashboard'
  }

  const handleResendVerification = async (email: string) => {
    if (!email?.trim()) {
      toast.error('Bitte geben Sie zuerst Ihre E-Mail-Adresse ein.')
      return
    }

    try {
      setResendingVerification(true)
      await resendSignupEmailApi(email.trim())
      toast.success('Die Bestätigungs-E-Mail wurde erneut gesendet.')
    } catch (error: any) {
      toast.error(error?.message || 'Die Bestätigungs-E-Mail konnte nicht erneut gesendet werden.')
    } finally {
      setResendingVerification(false)
    }
  }

  const formik = useFormik<SigninData>({
    initialValues: INITIAL_SIGNIN_VALUES,
    validationSchema: SigninSchema,
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: true,
    onSubmit: async (values, { setSubmitting, setStatus }) => {
      try {
        setAuthErrorCode(undefined)

        const result = await loginApi(values.email, values.password)

        const normalizedRole = normalizeRole(result.role)
        const target = redirectByRole(normalizedRole)

        setUser({ ...result, role: normalizedRole })
        navigate(target, { replace: true })
      } catch (error: any) {
        const backendCode = error?.code
        const backendMessage =
          error?.message || 'Fehlerhafte Anmeldeinformationen. Bitte versuchen Sie es erneut.'

        setAuthErrorCode(backendCode)

        if (backendCode === 'ACCOUNT_NOT_SETUP') {
          setStatus(
            'Ihre E-Mail-Adresse wurde noch nicht bestätigt. Bitte prüfen Sie Ihr Postfach oder senden Sie die Bestätigungs-E-Mail erneut.'
          )
          toast.error(
            'Ihre E-Mail-Adresse wurde noch nicht bestätigt. Bitte prüfen Sie Ihr Postfach oder senden Sie die Bestätigungs-E-Mail erneut.'
          )
          return
        }
        if (backendCode === 'UNAUTHORIZED') {
          toast.error('Fehlerhafte Anmeldeinformationen. Bitte versuchen Sie es erneut.')
          return
        }

        // setStatus(backendMessage)
        toast.error(backendMessage)
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      position="relative"
      overflow="hidden"
      direction="column"
      gap={10}
    >
      <Box
        position="absolute"
        inset={0}
        zIndex={0}
        bg={{
          base: 'conic-gradient(from 217.84deg at 62.79% 55.35%, #DCFBD4 -15.58deg, #FFFFFF 20.77deg, #8BF771 77.88deg, #FFFFFF 154.04deg, #FFFFFF 294.23deg, #DCFBD4 344.42deg, #FFFFFF 380.77deg)',
          _dark:
            'conic-gradient(from 217.84deg at 62.79% 55.35%, #052e16 -15.58deg, #020617 20.77deg, #166534 77.88deg, #020617 154.04deg, #020617 294.23deg, #052e16 344.42deg, #020617 380.77deg)',
        }}
      />

      <Box position="absolute" inset={0} bg="bgAlpha" backdropFilter="blur(10px)" zIndex={1} />

      <Box position="relative" zIndex={2} maxW="7xl" width="100%" px={{ base: 4, md: 8 }}>
        <Text
          textAlign="left"
          color="fg"
          mb={{ base: 4, md: 10 }}
          mt={{ base: 4, md: 0 }}
          lineHeight={{ base: '32px', md: '44px' }}
          fontWeight="semibold"
          fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
        >
          Bürosoftware für selbständige Unternehmensberater / Coaches
        </Text>

        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align="flex-start"
          width="100%"
          maxW="1280px"
          gap={{ base: 8, md: 16 }}
        >
          <Box flex={1} maxW={{ base: '100%', md: '600px' }} minW="300px">
            <Text textStyle="lgNormal" fontSize={{ base: 'sm', md: 'lg' }} mb={{ base: 3, lg: 5 }}>
              Die Software bietet Ihnen eine Vielzahl an Funktionen zur Optimierung der
              Geschäftsabläufe. Das Programm beinhaltet u.a. Rechnungsstellung, Analysefunktionen,
              Kundendatenbank uvm.
            </Text>
            <Button
              variant="solid"
              size="lg"
              bg="gray.solid"
              color="gray.contrast"
              textStyle="mdMedium"
              onClick={() => navigate('/register')}
            >
              Zugang erwerben
            </Button>
          </Box>

          <Box w={{ base: '100%', md: '600px' }}>
            <chakra.form
              onSubmit={formik.handleSubmit}
              bg="bg"
              p={{ base: 4, md: 6 }}
              rounded="2xl"
              shadow="md"
              w="100%"
            >
              <Field.Root
                required
                mb={4}
                invalid={!!(formik.touched.email && formik.errors.email)}
                textStyle="smMedium"
                color="fg"
              >
                <Field.Label>E-Mail</Field.Label>
                <Input
                  type="email"
                  autoComplete="username"
                  {...formik.getFieldProps('email')}
                  onChange={e => {
                    formik.handleChange(e)
                    formik.setStatus(undefined)
                    setAuthErrorCode(undefined)
                  }}
                />
                {formik.touched.email && formik.errors.email && (
                  <Field.ErrorText>{formik.errors.email}</Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root
                required
                mb={4}
                invalid={!!(formik.touched.password && formik.errors.password)}
                textStyle="smMedium"
                color="fg"
              >
                <Field.Label>Kennwort</Field.Label>
                <PasswordInput
                  type="password"
                  autoComplete="current-password"
                  {...formik.getFieldProps('password')}
                  onChange={e => {
                    formik.handleChange(e)
                    formik.setStatus(undefined)
                    setAuthErrorCode(undefined)
                  }}
                />
                {formik.touched.password && formik.errors.password && (
                  <Field.ErrorText>{formik.errors.password}</Field.ErrorText>
                )}
              </Field.Root>

              <Flex justify="space-between" align="center" mt={4}>
                <Link
                  color="gray.fg"
                  textStyle="xsMedium"
                  onClick={() => navigate('/forgot-password')}
                >
                  Kennwort vergessen?
                </Link>

                <Button
                  type="submit"
                  variant="surface"
                  disabled={!formik.isValid || !formik.dirty}
                  loading={formik.isSubmitting}
                  textStyle="mdMedium"
                  color="gray.fg"
                >
                  Anmelden
                  <LuLogIn />
                </Button>
              </Flex>

              {formik.status && (
                <Text color="red.500" mt={3} fontSize="sm" textAlign="center">
                  {formik.status}
                </Text>
              )}

              {authErrorCode === 'ACCOUNT_NOT_SETUP' && (
                <Flex mt={3} justify="center">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleResendVerification(formik.values.email)}
                    loading={resendingVerification}
                  >
                    Bestätigungs-E-Mail erneut senden
                  </Button>
                </Flex>
              )}
            </chakra.form>

            <Flex justify="flex-end" align="center" mt={4} gap={2}>
              <Text textStyle="xlSemibold" color="fg">
                Powered by
              </Text>
              <Image src={Logo} alt="Logo" height="30px" width="126px" objectFit="contain" />
            </Flex>
          </Box>
        </Flex>
      </Box>

      <Flex position="absolute" bottom={0} insetX={0} zIndex={2} justify="center" py={3}>
        <Link
          href="https://www.polygran.de/impressum/"
          target="_blank"
          rel="noopener noreferrer"
          color="fg.muted"
          fontSize="xs"
        >
          Impressum
        </Link>
      </Flex>
    </Flex>
  )
}
